import type { LintFinding } from '@ralph/contracts'
import { Plus, X } from 'lucide-react'
import { useId } from 'react'
import { BuilderSection, SECTION_IDS } from '@/components/marketplace/maker/BuilderShell.tsx'
import type { BuilderDraft } from '@/components/marketplace/maker/builder-form.ts'
import { findingsFor, LintNotes } from '@/components/marketplace/maker/LintNotes.tsx'
import { STARTER_MAX, STARTER_MAX_COUNT } from '@/components/marketplace/maker/maker-limits.ts'
import { Button, IconButton, Input } from '@/components/ui/primitives.tsx'
import { useT } from '@/i18n/index.ts'

/**
 * Example questions, capped at four by the contract.
 *
 * The lead says they fill the message box rather than send it, and that is a security
 * property as much as a courtesy: a starter that auto-sent would let a maker fire a
 * consequential request with the reader's own authority on a single tap (PRD-18's
 * instruction-attack section).
 */
export const StartersSection = ({
  step,
  draft,
  disabled,
  lint,
  onChange
}: {
  step: number
  draft: BuilderDraft
  disabled: boolean
  lint: LintFinding[]
  onChange: (patch: Partial<BuilderDraft>) => void
}) => {
  // In the body, never at module scope (PRD-27 F-3). The cap beside it is NOT copy and lives in
  // `maker-limits.ts`, so it cannot drift per locale (LOC-017).
  const strings = useT().marketplace.maker.sections.starters
  const fieldId = useId()

  const setStarter = (index: number, value: string) =>
    onChange({ starters: draft.starters.map((starter, position) => (position === index ? value : starter)) })

  return (
    <BuilderSection id={SECTION_IDS.starters} step={step} title={strings.title} lead={strings.lead}>
      <ul className="space-y-2">
        {draft.starters.map((starter, index) => (
          // Starters have no identity of their own, and keying on the text would remount the
          // input on every keystroke and lose the caret.
          // biome-ignore lint/suspicious/noArrayIndexKey: position IS the identity here
          <li key={`starter-${index}`} className="flex items-center gap-2">
            {/* The ordinal is a label, not decoration: four identical text fields in a row are
                otherwise indistinguishable to anyone reading the page a line at a time. */}
            <span aria-hidden className="readout w-3 shrink-0 text-right text-fg-subtle">
              {index + 1}
            </span>
            <Input
              id={`${fieldId}-starter-${index}`}
              value={starter}
              maxLength={STARTER_MAX}
              disabled={disabled}
              placeholder={strings.placeholder}
              aria-label={`${strings.title} ${index + 1}`}
              onChange={(event) => setStarter(index, event.target.value)}
            />
            <IconButton
              label={strings.remove(index)}
              tone="alert"
              size={30}
              disabled={disabled}
              onClick={() => onChange({ starters: draft.starters.filter((_, position) => position !== index) })}
            >
              <X size={13} strokeWidth={2.1} aria-hidden />
            </IconButton>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          icon={<Plus size={13} strokeWidth={2.4} aria-hidden />}
          disabled={disabled || draft.starters.length >= STARTER_MAX_COUNT}
          onClick={() => onChange({ starters: [...draft.starters, ''] })}
        >
          {strings.add}
        </Button>
        {draft.starters.length >= STARTER_MAX_COUNT && <span className="readout text-fg-subtle">{strings.full}</span>}
      </div>

      <LintNotes findings={findingsFor(lint, 'starters')} />
    </BuilderSection>
  )
}
